// src/pages/VerifyPhone.js
import React, { useState, useEffect } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../firebase-config";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_CUST =
  process.env.REACT_APP_API_CUST || "http://localhost:5000/api/user";

const VerifyPhone = () => {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [message, setMessage] = useState("");
  const [step, setStep] = useState(1);

  const navigate = useNavigate();
  const email = localStorage.getItem("userEmail");

  useEffect(() => {
    if (!email) navigate("/login");
  }, [email, navigate]);

  // 🧩 reCAPTCHA setup
  const setupRecaptcha = () => {
    if (!auth || typeof window === "undefined") {
      setMessage("Something went wrong. Try again.");
      return;
    }

    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
    }

    try {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {},
          "expired-callback": () => {},
        }
      );

      window.recaptchaVerifier.render().then((widgetId) => {
        window.recaptchaWidgetId = widgetId;
      });
    } catch (err) {
      console.error("❌ reCAPTCHA setup failed:", err);
      setMessage("Could not initialize reCAPTCHA.");
    }
  };

  // 📤 Send OTP
  const handleSendOTP = async () => {
    if (!phone.startsWith("+1")) {
      setMessage("Use +1 format for Canadian numbers.");
      return;
    }

    try {
      const check = await axios.post(`${API_CUST}/check-phone`, { phone });
      if (check.status === 409) {
        setMessage("Phone number already in use.");
        return;
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setMessage(err.response.data.message);
        return;
      }
    }

    setupRecaptcha();

    try {
      const appVerifier = window.recaptchaVerifier;
      const result = await signInWithPhoneNumber(auth, phone, appVerifier);
      setConfirmationResult(result);
      setStep(2);
      setMessage("OTP sent. Check your phone.");
    } catch (err) {
      console.error("❌ Firebase OTP error:", err);
      setMessage("Failed to send OTP. Check the phone number.");
    }
  };

  // 🔐 Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setMessage("Please enter the OTP.");
      return;
    }

    try {
      await confirmationResult.confirm(otp);

      await axios.post(`${API_CUST}/save-phone`, { email, phone });

      localStorage.setItem("userPhone", phone);
      setMessage("Phone verified successfully.");
      setTimeout(() => navigate("/set-address"), 800);
    } catch (err) {
      console.error("❌ OTP verification failed:", err);
      setMessage("Invalid or expired OTP. Try again.");
    }
  };

  return (
    <div className="min-h-screen bg-white px-6 py-12">
      <div className="max-w-md mx-auto space-y-6">

        {/* Header */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">
            Verify your phone
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            We’ll send you a one-time code to confirm your number
          </p>
        </div>

        {step === 1 ? (
          <div className="space-y-4">
            <input
              type="tel"
              placeholder="+1XXXXXXXXXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <button
              onClick={handleSendOTP}
              className="w-full py-3 rounded-full font-semibold text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:opacity-90 active:scale-95 transition"
            >
              Send code
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Enter verification code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <button
              onClick={handleVerifyOTP}
              className="w-full py-3 rounded-full font-semibold text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:opacity-90 active:scale-95 transition"
            >
              Verify & continue
            </button>
          </div>
        )}

        {message && (
          <p className="text-sm text-red-500 text-center">
            {message}
          </p>
        )}

        {/* reCAPTCHA container (hidden) */}
        <div id="recaptcha-container" />
      </div>
    </div>
  );
};

export default VerifyPhone;
